import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { achievements } from '../../data/constants';

// Achievements Container
const AchievementsContainer = styled.div`
  width: 100%;
  padding: 60px 0 80px 0;
  display: flex;
  justify-content: center;
  background: transparent;
  
  @media (max-width: 960px) {
    padding: 40px 0 60px 0;
  }
`;

// Wrapper to hold content
const AchievementsWrapper = styled.div`
  width: 100%;
  max-width: 1350px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  padding: 0 16px;
`;

// Title of the Section
const AchievementsTitle = styled.h1`
  font-family: 'Mosca Laroke', 'Playfair Display', 'Bodoni Moda', 'Cinzel', serif;
  font-size: 76px;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  line-height: 1;
  color: ${({ theme }) => theme.primary};
  margin-bottom: 32px;
  letter-spacing: 0.5px;

  @media (max-width: 1400px) { font-size: 64px; }
  @media (max-width: 1200px) { font-size: 52px; }
  @media (max-width: 768px)  { font-size: 44px; }
`;

// List of achievements
const AchievementList = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  padding: 0 16px;
  max-width: 1200px;
  
  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  @media (max-width: 640px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

// Get category color
const getCategoryGradient = (category) => {
  switch (category) {
    case 'competition':
      return 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)';
    case 'work':
      return 'linear-gradient(135deg, #0056D2 0%, #0073E6 100%)';
    case 'academic':
      return 'linear-gradient(135deg, #00C853 0%, #00EA64 100%)';
    case 'research':
      return 'linear-gradient(135deg, #E91E63 0%, #FF5C8D 100%)';
    case 'leadership':
      return 'linear-gradient(135deg, #00A3A3 0%, #00BFA5 100%)';
    default:
      return 'linear-gradient(135deg, #854CE6 0%, #9D5CE8 100%)';
  }
};

const getCategoryBorderColor = (category) => {
  switch (category) {
    case 'competition':
      return '#FFD700';
    case 'work':
      return '#0056D2';
    case 'academic':
      return '#00C853';
    case 'research':
      return '#E91E63';
    case 'leadership':
      return '#00BFA5';
    default:
      return '#854CE6';
  }
};

// Individual Achievement Card
const AchievementCard = styled.div`
  background: ${({ theme }) => theme.card};
  border: 0.1px solid ${({ category }) => getCategoryBorderColor(category)};
  box-shadow: rgba(23, 92, 230, 0.15) 0px 4px 24px;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: all 0.3s ease-in-out;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: rgba(23, 92, 230, 0.3) 0px 8px 32px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ category }) => getCategoryGradient(category)};
  }
`;

// Category Badge
const CategoryBadge = styled.div`
  background: ${({ category }) => getCategoryGradient(category)};
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  align-self: flex-start;
  letter-spacing: 0.5px;
`;

// Achievement Title inside each card
const AchievementCardTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  line-height: 1.4;
  margin: 0;
`;

// Meta Info Container
const MetaInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 8px 0;
`;

// Meta Item
const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.text_secondary};
`;

const MetaIcon = styled.span`
  font-size: 14px;
`;

// Achievement Description inside each card
const AchievementCardDescription = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.6;
  margin: 0;
  flex: 1;
`;

// Call-to-Action Button
const ViewButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  background: ${({ category }) => getCategoryGradient(category)};
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.3s ease-in-out;
  cursor: pointer;
  margin-top: auto;
  
  &:hover {
    transform: scale(1.02);
    box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

function AchievementSection() {
  return (
    <AchievementsContainer id="achievement">
      <AchievementsWrapper
        as={motion.div}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <AchievementsTitle>ACHIEVEMENTS</AchievementsTitle>
        <AchievementList>
          {achievements.map((achievement) => (
            <AchievementCard key={achievement.id} category={achievement.category}>
              <CategoryBadge category={achievement.category}>
                {achievement.category}
              </CategoryBadge>
              <AchievementCardTitle>{achievement.title}</AchievementCardTitle>

              <MetaInfo>
                <MetaItem>
                  <MetaIcon>📅</MetaIcon>
                  {achievement.date}
                </MetaItem>
                <MetaItem>
                  <MetaIcon>🏢</MetaIcon>
                  {achievement.organization}
                </MetaItem>
              </MetaInfo>

              <AchievementCardDescription>
                {achievement.description}
              </AchievementCardDescription>

              <ViewButton
                href={achievement.link}
                target="_blank"
                rel="noopener noreferrer"
                category={achievement.category}
              >
                View Details →
              </ViewButton>
            </AchievementCard>
          ))}
        </AchievementList>
      </AchievementsWrapper>
    </AchievementsContainer>
  );
}

export default AchievementSection;